/**
 * Frontend-only (offline) API — stores companies, jobs, and form data in localStorage.
 * Used when OFFLINE_MODE is enabled so the demo works without any backend.
 */

const DB_KEY = "VISHWAS_OFFLINE_DB_V1";

const COMPANY_ROOTS = {
  "/api/autocompany": "auto",
  "/api/tractioncompany": "traction",
  "/api/vconnectcompany": "vconnect",
  "/api/test_autocompany": "test_auto",
  "/api/test_tractioncompany": "test_traction",
  "/api/test_vconnectcompany": "test_vconnect",
};

const DATA_ROOTS = {
  "/api/autoData": { bag: "auto", dataKey: "autoTransformerData" },
  "/api/tractionData": { bag: "traction", dataKey: "TractionData" },
  "/api/vconnectData": { bag: "vconnect", dataKey: "vConnectData" },
  "/api/test_autoData": { bag: "auto", dataKey: "autoTransformerData" },
  "/api/test_tractionData": { bag: "traction", dataKey: "TractionData" },
  "/api/test_vconnectData": { bag: "vconnect", dataKey: "vConnectData" },
  "/api/test_autoTransformerData": { bag: "test_auto_xfmr", dataKey: "testAutoData" },
};

function defaultDb() {
  return {
    companies: {
      auto: [],
      traction: [],
      vconnect: [],
      test_auto: [],
      test_traction: [],
      test_vconnect: [],
    },
    docs: {}, // `${bag}|${company}|${project}` -> { projectName, companyName, [dataKey]: { stageN: { formM: {} } } }
    users: [
      {
        id: "demo-etc",
        name: "ETC Admin",
        email: "etcadmin@vishvaspower.com",
        password: "EtcAdmin@123",
        role: "etcadmin",
      },
      {
        id: "demo-admin",
        name: "Main Admin",
        email: "admin@vishvaspower.com",
        password: "Admin@123",
        role: "admin",
      },
    ],
  };
}

function loadDb() {
  try {
    const raw = localStorage.getItem(DB_KEY);
    if (!raw) return defaultDb();
    const parsed = JSON.parse(raw);
    return {
      ...defaultDb(),
      ...parsed,
      companies: { ...defaultDb().companies, ...(parsed.companies || {}) },
      docs: parsed.docs || {},
      users: parsed.users?.length ? parsed.users : defaultDb().users,
    };
  } catch {
    return defaultDb();
  }
}

function saveDb(db) {
  localStorage.setItem(DB_KEY, JSON.stringify(db));
}

function parseUrl(url = "") {
  try {
    if (url.startsWith("http") || url.startsWith("offline:")) {
      const u = new URL(url.replace(/^offline:\/\//, "https://"));
      return { pathname: u.pathname, searchParams: u.searchParams };
    }
  } catch {
    /* fall through */
  }
  const [pathPart, query = ""] = String(url).split("?");
  return {
    pathname: pathPart || "/",
    searchParams: new URLSearchParams(query),
  };
}

function matchCompanyRoot(pathname) {
  const keys = Object.keys(COMPANY_ROOTS).sort((a, b) => b.length - a.length);
  for (const root of keys) {
    if (pathname === root || pathname.startsWith(`${root}/`)) {
      return { root, bag: COMPANY_ROOTS[root], action: pathname.slice(root.length) || "/" };
    }
  }
  return null;
}

function matchDataRoot(pathname) {
  const keys = Object.keys(DATA_ROOTS).sort((a, b) => b.length - a.length);
  for (const root of keys) {
    if (pathname === root || pathname.startsWith(`${root}/`)) {
      return {
        root,
        ...DATA_ROOTS[root],
        action: pathname.slice(root.length) || "/",
      };
    }
  }
  return null;
}

function findCompany(db, bag, companyName) {
  return (db.companies[bag] || []).find((c) => c.companyName === companyName);
}

function findProject(company, projectName) {
  return (company?.companyProjects || []).find((p) => p.name === projectName);
}

function docKey(bag, companyName, projectName) {
  return `${bag}|${companyName}|${projectName}`;
}

function ensureDoc(db, bag, dataKey, companyName, projectName) {
  const key = docKey(bag, companyName, projectName);
  if (!db.docs[key]) {
    db.docs[key] = {
      projectName,
      companyName,
      [dataKey]: {},
    };
  }
  if (!db.docs[key][dataKey]) db.docs[key][dataKey] = {};
  return db.docs[key];
}

function ok(data, status = 200) {
  return { status, data };
}

function err(message, status = 400) {
  const error = new Error(message);
  error.response = { status, data: { message } };
  throw error;
}

function bodyOf(config) {
  let data = config.data;
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch {
      data = {};
    }
  }
  if (data instanceof FormData) {
    const obj = {};
    data.forEach((v, k) => {
      obj[k] = v;
    });
    return obj;
  }
  return data && typeof data === "object" ? data : {};
}

function handleAuth(method, pathname, body) {
  const db = loadDb();
  if (pathname === "/api/health") {
    return ok({ ok: true, service: "vishwas-offline", mode: "frontend-only" });
  }
  if (pathname === "/api/auth/login" && method === "POST") {
    const user = db.users.find(
      (u) =>
        u.email?.toLowerCase() === String(body.email || "").toLowerCase() &&
        u.password === body.password
    );
    if (!user) err("Invalid email or password", 401);
    return ok({
      token: `offline-token-${user.id}`,
      user: {
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.role,
      },
    });
  }
  if (pathname === "/api/auth/register" && method === "POST") {
    if (db.users.some((u) => u.email === body.email)) {
      err("User already exists", 400);
    }
    const user = {
      id: `u-${Date.now()}`,
      name: body.name || body.email,
      email: body.email,
      password: body.password,
      role: body.role || "etcadmin",
    };
    db.users.push(user);
    saveDb(db);
    return ok({
      token: `offline-token-${user.id}`,
      user: { id: user.id, name: user.name, email: user.email, role: user.role },
    });
  }
  if (pathname === "/api/auth/validate-token") {
    return ok({ valid: true });
  }
  if (pathname === "/api/auth/allUsers") {
    return ok(db.users.map(({ password, ...u }) => u));
  }
  return null;
}

function handleCompany(method, match, body, searchParams) {
  const db = loadDb();
  const { bag, action } = match;
  const list = db.companies[bag] || (db.companies[bag] = []);

  if ((action === "/" || action === "") && method === "GET") {
    return ok(list);
  }

  if ((action === "/" || action === "") && method === "POST") {
    if (!body.companyName || !body.companyDescription) {
      err("Company name and description are required");
    }
    if (list.some((c) => c.companyName === body.companyName)) {
      err("Company with this name already exists");
    }
    const company = {
      id: Date.now(),
      companyName: body.companyName,
      companyDescription: body.companyDescription,
      companyProjects: [],
      status: "active",
      createdAt: new Date().toISOString().split("T")[0],
      departmentType: body.departmentType || "",
    };
    list.push(company);
    saveDb(db);
    return ok(company, 201);
  }

  if (action === "/addCompany" && method === "POST") {
    const company = findCompany(db, bag, body.companyName);
    if (!company) err(`Company '${body.companyName}' not found`, 404);
    const project = body.companyProjects;
    if (!project?.name) err("Project payload required");
    if (findProject(company, project.name)) {
      err(`Project '${project.name}' already exists`);
    }
    company.companyProjects = company.companyProjects || [];
    company.companyProjects.push({
      ...project,
      lastActivity: project.lastActivity || new Date().toISOString(),
    });
    saveDb(db);
    return ok({
      message: `Project '${body.projectName || project.name}' added and linked to company '${body.companyName}'.`,
      project: company,
    });
  }

  if (action === "/updateFormsCompleted" && method === "POST") {
    const company = findCompany(db, bag, body.companyName);
    if (!company) err("Company or project not found", 404);
    const project = findProject(company, body.projectName);
    if (!project) err("Company or project not found", 404);

    project.lastActivity = new Date().toISOString();
    if (body.completeUnloading) {
      project.stageApprovals = { ...(project.stageApprovals || {}), 0: true };
      project.submittedStages = { ...(project.submittedStages || {}), 0: true };
      project.unloadingRequired = true;
      project.stage = 1;
      project.formsCompleted = 0;
      project.status = "in-progress";
      if (body.totalForms != null) project.totalForms = body.totalForms;
    } else {
      project.formsCompleted = Math.max(
        Number(project.formsCompleted) || 0,
        Number(body.formsCompleted) || 0
      );
      if (body.status) project.status = body.status;
      if (body.stage !== undefined && body.stage !== null && body.stage !== "") {
        project.submittedStages = {
          ...(project.submittedStages || {}),
          [String(body.stage)]: true,
        };
      }
    }
    saveDb(db);
    return ok({
      message: `Forms completed and status for project '${body.projectName}' successfully updated.`,
      project,
    });
  }

  if (action === "/approveCompanyStage" && method === "POST") {
    const company = findCompany(db, bag, body.companyName);
    const project = findProject(company, body.projectName);
    if (!project) err("Project not found", 404);
    const stageNumber = Number(body.stage);
    project.stageApprovals = { ...(project.stageApprovals || {}), [stageNumber]: true };
    project.formsCompleted = 0;
    if (stageNumber !== 6 && stageNumber !== 7) {
      project.stage = Math.max(Number(project.stage) || 0, stageNumber + 1);
      project.status = "in-progress";
    } else {
      project.status = "completed";
    }
    project.lastActivity = new Date().toISOString();
    saveDb(db);
    return ok({ message: `Stage '${stageNumber}' approved.`, project });
  }

  if (action === "/rejectStage" && method === "POST") {
    const company = findCompany(db, bag, body.companyName);
    const project = findProject(company, body.projectName);
    if (!project) err("Project not found", 404);
    const stageNumber = Number(body.stage);
    project.stageApprovals = { ...(project.stageApprovals || {}), [stageNumber]: false };
    project.submittedStages = { ...(project.submittedStages || {}), [stageNumber]: false };
    project.status = "rejected";
    project.rejectionReason = body.rejectionReason || "";
    saveDb(db);
    return ok({ message: `Stage '${stageNumber}' rejected.`, project });
  }

  if (action === "/editCompanyName" && method === "PUT") {
    const company = findCompany(db, bag, body.oldCompanyName || body.companyName);
    if (!company) err("Company not found", 404);
    const next = body.newCompanyName;
    if (!next) err("New company name required");
    if (list.some((c) => c.companyName === next)) err("Company name already exists");
    const old = company.companyName;
    company.companyName = next;
    (company.companyProjects || []).forEach((p) => {
      p.companyName = next;
    });
    // rename docs
    Object.keys(db.docs).forEach((k) => {
      if (k.startsWith(`${bag}|${old}|`)) {
        const rest = k.slice(`${bag}|${old}|`.length);
        db.docs[`${bag}|${next}|${rest}`] = {
          ...db.docs[k],
          companyName: next,
        };
        delete db.docs[k];
      }
    });
    saveDb(db);
    return ok({ message: "Company name updated.", company });
  }

  if (action === "/editProjectName" && method === "PUT") {
    const company = findCompany(db, bag, body.companyName);
    const project = findProject(company, body.oldProjectName);
    if (!project) err("Project not found", 404);
    if (findProject(company, body.newProjectName)) err("Project already exists");
    const old = project.name;
    project.name = body.newProjectName;
    const oldKey = docKey(bag, body.companyName, old);
    if (db.docs[oldKey]) {
      db.docs[docKey(bag, body.companyName, body.newProjectName)] = {
        ...db.docs[oldKey],
        projectName: body.newProjectName,
      };
      delete db.docs[oldKey];
    }
    saveDb(db);
    return ok({ message: "Project name updated.", company });
  }

  if (action === "/deleteProject" && method === "DELETE") {
    const company = findCompany(db, bag, body.companyName);
    if (!company) err("Company not found", 404);
    company.companyProjects = (company.companyProjects || []).filter(
      (p) => p.name !== body.projectName
    );
    delete db.docs[docKey(bag, body.companyName, body.projectName)];
    saveDb(db);
    return ok({ message: "Project deleted.", company });
  }

  if (action === "/deleteCompany" && method === "DELETE") {
    const name = body.companyName;
    db.companies[bag] = list.filter((c) => c.companyName !== name);
    Object.keys(db.docs).forEach((k) => {
      if (k.startsWith(`${bag}|${name}|`)) delete db.docs[k];
    });
    saveDb(db);
    return ok({ message: "Company deleted." });
  }

  err(`Offline API: unsupported company route ${method} ${match.root}${action}`, 404);
}

function handleData(method, match, body, searchParams, pathname) {
  const db = loadDb();
  const { bag, dataKey, action } = match;

  const companyName =
    body.companyName || searchParams.get("companyName") || "";
  const projectName =
    body.projectName || searchParams.get("projectName") || "";
  let stage = body.stage ?? searchParams.get("stage");
  let formNumber = body.formNumber ?? searchParams.get("formNumber");

  const stageFormMatch = String(action).match(/\/getTable\/Stage(\d+)Form(\d+)/i);
  if (stageFormMatch) {
    stage = stageFormMatch[1];
    formNumber = stageFormMatch[2];
  }

  if (
    (action === "/getTable" ||
      action.startsWith("/getTable/") ||
      action === "/getFormData") &&
    (method === "GET" || method === "POST")
  ) {
    if (!projectName || !companyName || stage === undefined || stage === null || stage === "" || formNumber === undefined || formNumber === null || formNumber === "") {
      return ok({ message: "No saved form data yet.", data: {} });
    }
    const doc = ensureDoc(db, bag, dataKey, companyName, projectName);
    const formData = doc[dataKey]?.[`stage${stage}`]?.[`form${formNumber}`] || {};
    return ok({
      message: Object.keys(formData).length
        ? `Data for stage ${stage}, form ${formNumber} retrieved successfully`
        : "No saved form data yet.",
      data: formData,
    });
  }

  if (action === "/getStageTable" && method === "POST") {
    const doc = ensureDoc(db, bag, dataKey, companyName, projectName);
    const stageData = doc[dataKey]?.[`stage${stage}`] || {};
    return ok({
      message: `Data for stage ${stage} retrieved successfully`,
      data: stageData,
    });
  }

  if (action === "/getCompleteTable" && method === "POST") {
    const doc = ensureDoc(db, bag, dataKey, companyName, projectName);
    return ok({
      message: "Complete table retrieved",
      data: doc,
    });
  }

  if (action === "/setTable" && method === "POST") {
    if (!projectName || !companyName || formNumber === undefined || stage === undefined) {
      err("Project name, company name, form number, and stage are required.");
    }
    const doc = ensureDoc(db, bag, dataKey, companyName, projectName);
    const meta = ["projectName", "companyName", "stage", "formNumber"];
    const parsed = {};
    Object.entries(body).forEach(([k, v]) => {
      if (meta.includes(k)) return;
      parsed[k] = v;
    });
    if (!doc[dataKey][`stage${stage}`]) doc[dataKey][`stage${stage}`] = {};
    doc[dataKey][`stage${stage}`][`form${formNumber}`] = parsed;
    saveDb(db);
    return ok({ message: "Form data saved successfully", data: doc }, 201);
  }

  if (action === "/download-all-forms" && method === "POST") {
    err(
      "PDF download needs a backend. Form data is saved locally for this frontend-only demo.",
      501
    );
  }

  err(`Offline API: unsupported data route ${method} ${pathname}`, 404);
}

function handleLegacyTable(method, pathname, searchParams) {
  if (method !== "GET") return null;
  const m = pathname.match(/^\/api\/table\/getTable\/Stage(\d+)Form(\d+)$/i);
  if (!m) return null;
  const stage = m[1];
  const formNumber = m[2];
  const companyName = searchParams.get("companyName") || "";
  const projectName = searchParams.get("projectName") || "";
  const db = loadDb();

  for (const [bag, dataKey] of [
    ["traction", "TractionData"],
    ["vconnect", "vConnectData"],
    ["auto", "autoTransformerData"],
  ]) {
    const doc = db.docs[docKey(bag, companyName, projectName)];
    const formData = doc?.[dataKey]?.[`stage${stage}`]?.[`form${formNumber}`];
    if (formData && Object.keys(formData).length) {
      return ok({
        message: `Data for Stage${stage}Form${formNumber} retrieved successfully`,
        data: formData,
      });
    }
  }
  return ok({ message: "No saved form data yet.", data: {} });
}

/**
 * Handle one offline request. Throws axios-like errors on failure.
 * @returns {{ status: number, data: any }}
 */
export function handleOfflineRequest(config = {}) {
  const method = String(config.method || "get").toUpperCase();
  const { pathname, searchParams } = parseUrl(config.url || "");
  const body = {
    ...bodyOf(config),
    ...(config.params || {}),
  };

  // Merge query into body for GET helpers
  searchParams.forEach((v, k) => {
    if (body[k] === undefined) body[k] = v;
  });

  const auth = handleAuth(method, pathname, body);
  if (auth) return auth;

  const legacy = handleLegacyTable(method, pathname, searchParams);
  if (legacy) return legacy;

  const company = matchCompanyRoot(pathname);
  if (company) return handleCompany(method, company, body, searchParams);

  const data = matchDataRoot(pathname);
  if (data) return handleData(method, data, body, searchParams, pathname);

  // Soft-ok for unknown GETs used by health-ish probes
  if (method === "GET") return ok({ message: "offline ok", path: pathname });

  err(`Offline API: no handler for ${method} ${pathname}`, 404);
}

export function clearOfflineDb() {
  localStorage.removeItem(DB_KEY);
}
